import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Tooltip, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import SoftButton from "StyleFeatures/SoftButton";
import { Link } from "react-router-dom";
import { Edit } from "@mui/icons-material";
import API from "api/endpoints";
import columns from "../../Pages/AdministratorDashboard/Hiring/JobPosting/HiringTableColumns";
import { JobPostingStatusEnum } from "Constants/Enums/JobPostingStatusEnum";
import { getPostingSkills } from "Common/skillHelpers";

import {
  getJobPostings,
  addJobPosting,
  updateJobPosting,
} from "Services/AdminServices/HiringService";
import { resetJobPostingForm } from "../../Pages/AdministratorDashboard/Hiring/JobPosting/resetFormFields";
import axiosInstance from "api/axiosInstance";

const { confirm } = Modal;

function useHiring() {
  const [form] = Form.useForm();
  const [jobPostings, setJobPostings] = useState([]);
  const [isCompensationEnabled, setIsCompensationEnabled] = useState(false);
  const [jobDescription, setJobDescription] = useState(
    `We provide equal employment opportunities to all employees and applicants for employment and prohibits discrimination and harassment of any type. 
    Datagonomix considers applicants for employment without regard to race, color, religion, age, sex, national origin, disability status, genetic information, 
    protected veteran status, sexual orientation, gender, gender identity or expression, or any other characteristic protected by federal, state, provincial 
    or local laws.`
  );
  const [showForm, setShowForm] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addloading, setAddLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [ jobTitles, setJobTitles] = useState([]);
  const [ selectedDepartmentId, setSelectedDepartmentId ] =  useState(null);
  const [ selectedJobTitle , setSelectedJobTitle] = useState(null);
  // Per Mudassir (May-24): JobPosting skills are now JobTitle-scoped.
  // `jobTitleDefaultSkills` are the canonical required skills for the
  // selected JobTitle (read-only chips). `availableSkills` is the
  // recruiter-selectable pool (department skills minus the JobTitle
  // defaults so duplicates can't be picked).
  const [jobTitleDefaultSkills, setJobTitleDefaultSkills] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillCreating, setSkillCreating] = useState(false);


  useEffect(() => {
    fetchJobPostings();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await axiosInstance.get(API.department.getList);
      setDepartments(response.data);
    } catch (error) {
      console.error("Error fetching Department List ");
      throw error;
    }
  };

  // Per Mudassir (May-24): skills are now JobTitle-scoped, not department-
  // scoped. Two calls fire on JobTitle change — one for the JobTitle's
  // default required skills (read-only chips) and one for the remaining
  // available skills the recruiter can pick from. The previous full-list
  // catalog fetch on mount is no longer needed.
  const fetchSkillsForJobTitle = async (jobTitleId) => {
    if (!jobTitleId) {
      setJobTitleDefaultSkills([]);
      setAvailableSkills([]);
      return { defaults: [], available: [] };
    }
    try {
      setSkillsLoading(true);
      const [defaultsRes, availableRes] = await Promise.all([
        axiosInstance.get(API.skills.getJobTitleSkills(jobTitleId)),
        axiosInstance.get(API.skills.getAvailableSkillsForJobTitle(jobTitleId)),
      ]);
      const defaults = defaultsRes.data || [];
      const available = availableRes.data || [];
      setJobTitleDefaultSkills(defaults);
      setAvailableSkills(available);
      return { defaults, available };
    } catch (error) {
      console.error("Error fetching JobTitle skills:", error);
      setJobTitleDefaultSkills([]);
      setAvailableSkills([]);
      return { defaults: [], available: [] };
    } finally {
      setSkillsLoading(false);
    }
  };

  const handleDepartmentChange = async(value) => {
      const deptId = value!= null ? Number(value) : null;
      setSelectedDepartmentId(deptId);
      // Department change invalidates the previous JobTitle selection and
      // its skill set. Reset both so the form doesn't carry stale skills
      // from the prior JobTitle into the new department's selection.
      setSelectedJobTitle(null);
      setJobTitleDefaultSkills([]);
      setAvailableSkills([]);
      form.setFieldsValue({ jobTitleId: undefined, skills: [] });
      const response = await axiosInstance.get(API.jobTitle.getListByLookup(deptId));
      setJobTitles(response.data);
  }

  const handleMarkAsFilled = (jobPostingId, jobTitle) => {
    confirm({
      title: "Mark this job posting as Filled?",
      content: (
        <span>
          <strong>{jobTitle || "This posting"}</strong> will be closed for further hiring
          actions (shortlist, interviews, feedback, document send/receive). Onboarding
          and post-onboarding actions for already-progressed applicants will remain
          available. This cannot be undone.
        </span>
      ),
      okText: "Yes, mark as Filled",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axiosInstance.put(API.hiringJobPosting.markAsFilled(jobPostingId));
          message.success("Job posting marked as Filled.");
          fetchJobPostings();
        } catch (error) {
          console.error("Failed to mark as filled:", error);
          message.error(
            error.response?.data?.details ||
              error.response?.data?.message ||
              "Failed to mark posting as Filled. Please try again."
          );
        }
      },
    });
  };

  const fetchJobPostings = async () => {
    setLoading(true);
    try {
      const response = await getJobPostings();
      // Get-All-Postings returns a flat array; the old Get-List returned
      // { postingDetail: [...] }. Handle both shapes so the table works
      // regardless of which endpoint backs the service.
      const postings = Array.isArray(response)
        ? response
        : response?.postingDetail || response?.postings || [];
      if (postings.length > 0) {
        const updatedRows = postings.map((info) => {
          const formattedCreatedOn = info.createdOn
            ? new Date(info.createdOn).toLocaleDateString()
            : "N/A";
          const dept = departments.find((d) => d.id === info.id);
          const isFilled = info.status === JobPostingStatusEnum.Filled;

          return {
            ...info,
            key: info.id.toString(),
            id: info.id,
            PositionName: info.jobTitle,
            departmentId: info.departmentId,
            departmentName: dept ? dept.name : info.departmentName,
            createdOn: formattedCreatedOn,
            skills: getPostingSkills(info).join("  ") || "N/A",
            isBudgetApproved: info.isBudgetApproved,
            managerId: info.managerId,
            employmentType: info.employmentType,
            maximumExperience: info.maximumExperience,
            compensation: info.compensation,
            jobLocationType: info.jobLocationType,
            jobType: info.jobType,
            jobDescription: info.jobDescription,
            jobInternalCode: info.jobInternalCode,
            skills: getPostingSkills(info),

ViewCandidate: (
  <SoftButton className="btn-back" size="small">
    <Link
      to={`/viewcandidate/${info.id}`}
      style={{
        color: "inherit",
        textDecoration: "none",
        display: "block",
        width: "100%"
      }}
    >
      View Candidate
    </Link>
  </SoftButton>
),
            // Action: Edit icon + Mark As Filled button.
            // Per Mudassir: once posting is Filled, hide the action button
            // (no further hiring activity allowed). Replace with a disabled
            // "Filled" chip so the row visually balances and HR sees a clear
            // "already actioned" confirmation rather than empty space.
            Action: (
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Tooltip title="Edit posting">
                  <Edit style={{ cursor: "pointer" }} onClick={() => handleEdit(info)} />
                </Tooltip>
                {isFilled ? (
                  <Tooltip title="This posting is closed for further hiring actions.">
                    <Button
                      size="small"
                      icon={<CheckCircleOutlined />}
                      disabled
                      style={{
                        borderRadius: 40,
                        fontWeight: 600,
                        background: "#f0f9f1",
                        borderColor: "#c7e3cd",
                        color: "#0f5a2a",
                        cursor: "not-allowed",
                      }}
                    >
                      Filled
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleMarkAsFilled(info.id, info.jobTitle)}
                    style={{
                      background: "#0f5a2a",
                      borderColor: "#0f5a2a",
                      borderRadius: 40,
                      fontWeight: 600,
                    }}
                  >
                    Mark As Filled
                  </Button>
                )}
              </span>
            ),
          };
        });
        setJobPostings(updatedRows);
      } else {
        setJobPostings([]);
      }
    } catch (error) {
      console.error("Error fetching job postings:", error);
      setJobPostings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleJobTitleChange = async(value) => {
     const id = value != null ? Number(value): null
     setSelectedJobTitle(id)
     // Skills selection resets on JobTitle change — the new available pool
     // may not include the previously picked extras.
     form.setFieldsValue({ skills: [] });
     await fetchSkillsForJobTitle(id);
    }

  // Tags-mode onChange for the Additional Skills field. Existing skills come
  // through as numeric IDs; anything the user types that isn't already in the
  // pool comes through as a string. For each new string we call the global
  // create-or-get endpoint, then swap the string for the returned ID in the
  // form value so the submit payload only carries IDs.
  const handleSkillsChange = async (selectedValues) => {
    const existingIds = selectedValues.filter((v) => typeof v === "number");
    const newNames = selectedValues
      .filter((v) => typeof v === "string")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (newNames.length === 0) return;

    setSkillCreating(true);
    try {
      const created = await Promise.all(
        newNames.map((name) =>
          axiosInstance
            .post(API.skills.createOrGet, { name })
            .then((r) => r.data)
        )
      );

      setAvailableSkills((prev) => {
        const next = [...prev];
        created.forEach((skill) => {
          const id = skill.id ?? skill.skillId;
          if (!next.some((s) => (s.id ?? s.skillId) === id)) {
            next.push(skill);
          }
        });
        return next;
      });

      const newIds = created.map((s) => s.id ?? s.skillId);
      form.setFieldsValue({ skills: [...existingIds, ...newIds] });
    } catch (error) {
      console.error("Failed to create skill:", error);
      message.error(
        error.response?.data?.details ||
          error.response?.data?.message ||
          "Failed to add new skill. Please try again."
      );
      form.setFieldsValue({ skills: existingIds });
    } finally {
      setSkillCreating(false);
    }
  };

 

  const handleEdit = async (info) => {
  const deptId = info.departmentId;
  const jobTitleId = info.jobTitleId;

  // Fetch job titles + JobTitle-scoped skill pools in parallel.
  // Per Mudassir (May-24): on edit we need both the JobTitle defaults
  // (readonly chips) and the available-extras pool (multi-select source).
  const [jobTitlesRes, skillsRes] = await Promise.all([
    axiosInstance.get(API.jobTitle.getListByLookup(deptId)),
    fetchSkillsForJobTitle(jobTitleId),
  ]);
  setJobTitles(jobTitlesRes.data);
  setSelectedDepartmentId(deptId);
  setSelectedJobTitle(jobTitleId);

  // Pre-select only the recruiter-added extras in the multi-select.
  // JobTitle defaults are shown as readonly chips and auto-merged by BE on
  // save, so they're filtered out of the pre-selection here.
  //
  // Get-All-Postings returns each posting's `skills` as an array of skill
  // NAMES (strings), not IDs — but the form value and the Update payload need
  // numeric IDs (BE rejects strings: "JSON value could not be converted to
  // System.Int32"). So we map names back to IDs using the JobTitle defaults +
  // available pool (both carry {id, name}), which together cover every global
  // skill the posting can hold. Object and numeric-ID shapes are handled too,
  // so this keeps working if BE later returns IDs directly.
  const skillLookup = [
    ...(skillsRes.defaults || []),
    ...(skillsRes.available || []),
  ];
  const nameToId = new Map();
  skillLookup.forEach((s) => {
    const id = s.id ?? s.skillId;
    const name = s.name ?? s.skillName;
    if (name != null && id != null) {
      nameToId.set(String(name).toLowerCase(), id);
    }
  });

  // Defaults can arrive as names or IDs depending on the source shape, so
  // match against both sets.
  const defaultNameSet = new Set(
    (skillsRes.defaults || []).map((s) =>
      String(s.name ?? s.skillName).toLowerCase()
    )
  );
  const defaultIdSet = new Set(
    (skillsRes.defaults || []).map((s) => s.id ?? s.skillId)
  );

  const isDefaultSkill = (s) => {
    if (s != null && typeof s === "object") {
      return defaultIdSet.has(s.id ?? s.skillId);
    }
    if (typeof s === "number") return defaultIdSet.has(s);
    return defaultNameSet.has(String(s).toLowerCase());
  };

  const resolveSkillId = (s) => {
    if (s != null && typeof s === "object") return s.id ?? s.skillId;
    if (typeof s === "number") return s;
    return nameToId.get(String(s).toLowerCase());
  };

  // Prefer the new `additionalSkills` field (already just the recruiter extras);
  // fall back to legacy `skills` minus the JobTitle defaults for older responses.
  const extraNames = Array.isArray(info.additionalSkills)
    ? info.additionalSkills
    : (Array.isArray(info.skills) ? info.skills : []).filter(
        (s) => !isDefaultSkill(s)
      );
  const extraSkillIds = extraNames
    .map(resolveSkillId)
    .filter((id) => id != null); // drop anything we couldn't resolve to an ID

  form.setFieldsValue({
    jobTitleId: info.jobTitleId,
    departmentId: String(info.departmentId),
    isBudgetApproved: info.isBudgetApproved ? "Yes" : "No",
    employmentType: info.employmentType,
    maximumExperience: info.maximumExperience,
    compensation: info.compensation,
    jobLocationType: info.jobLocationType,
    jobType: info.jobType,
    jobInternalCode: info.jobInternalCode,
    skills: extraSkillIds,
  });

  setEditingJobId(info.jobPostingId || info.id);
  setJobDescription(info.jobDescription);
  setShowForm(true);
};

  const handleAddJob = () => {
    form.resetFields();
    setEditingJobId(null);
    setShowForm(true);
  };

  const handleBackToList = () => {
    resetJobPostingForm(form);
    setEditingJobId(null);
    setSelectedDepartmentId(null);
    setSelectedJobTitle(null);
    setJobTitleDefaultSkills([]);
    setAvailableSkills([]);
    setShowForm(false);
  };

 const onFinish = async (values) => {
  const finalValues = {
    jobPostingId: editingJobId || 0, // 🔥 REQUIRED FIX
    jobTitleId: values.jobTitleId || 0,
    isBudgetApproved: values.isBudgetApproved === "Yes",
    status: values.status || 0,
    departmentId: parseInt(values.departmentId) || 0,
    employmentType: values.employmentType,
    maximumExperience: values.maximumExperience || "",
    compensation: isCompensationEnabled ? values.compensation : "0",
    jobLocationType: values.jobLocationType || "",
    jobType: values.jobType || "",
    jobInternalCode: values.jobInternalCode || "",
    jobDescription: jobDescription || "",
    skills: values.skills || [],
  };

  try {
    setAddLoading(true);
    if (editingJobId) {
      await updateJobPosting(finalValues);
    } else {
      await addJobPosting(finalValues);
    }
      message.success("Job posting submitted successfully.");
      setShowForm(false);
      fetchJobPostings();
      resetJobPostingForm(form);
    } catch (error) {
      console.error(" Error submitting:", error.response?.data || error.message);
      const errorMessages = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(", ")
        : "Failed to submit the job posting. Please try again.";
      message.error(errorMessages);
    } finally {
      setAddLoading(false);
    }
  };

  // Define columns for the table

  const handleBudgetApprovalChange = (value) => {
    setIsCompensationEnabled(value === "Yes");
    if (value === "No") {
      form.setFieldsValue({ compensation: "$0" });
    } else {
      form.setFieldsValue({ compensation: "" });
    }
  };

  return {
    handleBackToList,
    handleAddJob,
    form,
    jobPostings,
    showForm,
    loading,
    addloading,
    onFinish,
    columns,
    setShowForm,
    jobDescription,
    setJobDescription,
    editingJobId,
    isCompensationEnabled,
    handleBudgetApprovalChange,
    departments,
    jobTitles,
    handleDepartmentChange,
    selectedDepartmentId,
    selectedJobTitle,
    handleJobTitleChange,
    jobTitleDefaultSkills,
    availableSkills,
    skillsLoading,
    skillCreating,
    handleSkillsChange,
  };
}

export default useHiring;
